import * as yup from 'yup';
import { RISK_CATEGORIES, RISK_FILTER_OTHERS } from '../../constants/riskCategories';
import { classificationOptions } from '../../utils/controlConstants.js';

export const riskProcessAssociationSchema = yup
  .object({
    processId: yup.string().trim().required(),
    linkedNodeIds: yup.array().of(yup.string().trim().required()).default([]),
    mitigatingControlIds: yup.array().of(yup.string().trim().required()).default([]),
  })
  .required();

const riskDescriptionFieldSchema = yup
  .string()
  .trim()
  .required('Description is required')
  .min(10, 'Description must be at least 10 characters')
  .max(250, 'Description cannot be more than 250 characters');

/** Manual create on `/sox-risks/create` — description required, no min/max length. */
const riskManualCreateDescriptionFieldSchema = yup
  .string()
  .trim()
  .required('Description is required');

const classificationValues = classificationOptions.map((o) => o.value) as Array<'business' | 'itgc'>;

export const riskFormSchema = yup
  .object({
    clientRiskId: yup
      .string()
      .trim()
      .max(50, 'Reference ID cannot be more than 50 characters')
      .test(
        'clientRiskId-min-if-present',
        'Reference ID must be at least 3 characters',
        (value) => !value || value.length >= 3
      ),
    riskDescription: riskDescriptionFieldSchema,
    riskCategory: yup
      .string()
      .required('Risk Category is required')
      .oneOf([...RISK_CATEGORIES, RISK_FILTER_OTHERS], 'Please select a valid risk category'),
    riskLevel: yup
      .mixed<'High' | 'Medium' | 'Low'>()
      .oneOf(['High', 'Medium', 'Low'], 'Risk level is required')
      .required('Risk level is required'),
    classification: yup
      .mixed<'business' | 'itgc'>()
      .oneOf(classificationValues, 'Please select a valid classification')
      .default('business'),
    itgcDomainId: yup.string().trim().default(''),
    processAssociations: yup.array().of(riskProcessAssociationSchema).default([]),
  })
  .required();

export const riskManualCreateSchema = riskFormSchema.shape({
  riskDescription: riskManualCreateDescriptionFieldSchema,
  itgcDomainId: yup
    .string()
    .trim()
    .default('')
    .when('classification', {
      is: 'itgc',
      then: (schema) => schema.required('Domain is required.'),
      otherwise: (schema) => schema,
    }),
});

/** Manual edit on risk details — same description rules as manual create. */
export const riskManualEditSchema = riskManualCreateSchema;

/** Bulk upload draft review — aligned with manual create (description required, no min/max). */
export const riskBulkDraftSchema = riskManualCreateSchema;

export type RiskProcessAssociationFormValue = yup.InferType<typeof riskProcessAssociationSchema>;
export type RiskFormValues = yup.InferType<typeof riskFormSchema>;
